/**
 * เอกสารพนักงาน — ภาพรวมเอกสารที่มี/ขาดของทุกคน + ทวงเอกสารผ่าน LINE +
 * รายละเอียดเอกสารรายคน (อัปโหลด/ดูเวอร์ชัน) + สร้างสัญญาจ้างงาน.
 */
import { useMemo, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useToast } from '../../components/ui/toast';
import { useConsequence } from '../../components/ui/ConsequencePreview';
import { Modal } from '../../components/ui/Modal';
import { uploadFile, resolveFileUrl } from '../../lib/api-client';
import {
  useDocumentsOverview,
  useRemindDocuments,
  useEmployeeDocuments,
  useCreateEmployeeDocument,
  useGenerateContract,
  type EmployeeDocType,
} from '../../hooks';
import { EMPLOYEE_DOC_TYPE_LABELS, EMPLOYEE_DOC_TYPES } from '@payroll/shared';
import { CheckCircle2, XCircle, Bell, FileText, Upload, FileSignature } from 'lucide-react';

export function OwnerDocuments() {
  const { data: overview, isLoading } = useDocumentsOverview();
  const remindM = useRemindDocuments();
  const toast = useToast();
  const consequence = useConsequence();

  const [deptFilter, setDeptFilter] = useState('');
  const [selectedEmp, setSelectedEmp] = useState<string | null>(null);

  const employees = overview?.employees ?? [];
  const requiredTypes = overview?.required_types ?? [];
  const departments = useMemo(
    () => [...new Set(employees.map((e) => e.department).filter(Boolean))] as string[],
    [employees],
  );
  const filteredRaw = deptFilter ? employees.filter((e) => e.department === deptFilter) : employees;
  const filtered = useMemo(
    () =>
      [...filteredRaw].sort((a, b) => {
        const aPending = a.pending_sign_docs.length > 0;
        const bPending = b.pending_sign_docs.length > 0;
        if (aPending !== bPending) return aPending ? -1 : 1;
        if (aPending && bPending) {
          if (!a.start_date && !b.start_date) return 0;
          if (!a.start_date) return 1;
          if (!b.start_date) return -1;
          return a.start_date.localeCompare(b.start_date);
        }
        return 0;
      }),
    [filteredRaw],
  );

  const missingCount = filtered.reduce((sum, e) => {
    const missing = requiredTypes.filter((t) => !e.docs[t]?.present).length;
    return sum + (missing > 0 ? 1 : 0);
  }, 0);
  const pendingSignCount = filtered.reduce((sum, e) => sum + e.pending_sign_docs.length, 0);

  async function handleRemindMissing() {
    const targets = filtered.filter((e) => requiredTypes.some((t) => !e.docs[t]?.present));
    if (targets.length === 0) {
      toast.info('ไม่มีใครขาดเอกสารในตอนนี้');
      return;
    }
    const ok = await consequence({
      title: 'ทวงเอกสารที่ขาด',
      intro: 'ระบบจะส่งข้อความ LINE แจ้งพนักงานที่ยังส่งเอกสารไม่ครบ',
      lines: [{ label: 'จำนวนคนที่จะทวง', value: `${targets.length} คน`, emphasis: true }],
      effects: ['ส่งการ์ด LINE แจ้งรายการเอกสารที่ขาด', 'พนักงานที่ยังไม่ผูก LINE จะถูกข้าม'],
      confirmText: 'ทวงเอกสาร',
    });
    if (!ok) return;
    try {
      const r = await remindM.mutateAsync({ emp_codes: targets.map((e) => e.emp_code) });
      toast.success(`ทวงเอกสารแล้ว ${r.reminded} คน${r.skipped_no_line > 0 ? ` (ข้าม ${r.skipped_no_line} คนที่ยังไม่ผูก LINE)` : ''}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ทวงเอกสารไม่สำเร็จ');
    }
  }

  return (
    <AdminLayout title="เอกสารพนักงาน">
      <p className="text-sm text-gray-600 mb-3">ติดตามเอกสารที่จำเป็นของพนักงานแต่ละคน + สร้างสัญญาจ้างงาน + ทวงเอกสารที่ขาด</p>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="card p-3 text-center">
          <div className="text-2xl font-bold text-gray-900">{filtered.length}</div>
          <div className="text-xs text-gray-500">พนักงานทั้งหมด</div>
        </div>
        <div className="card p-3 text-center">
          <div className="text-2xl font-bold text-red-600">{missingCount}</div>
          <div className="text-xs text-gray-500">ขาดเอกสาร</div>
        </div>
        <div className="card p-3 text-center">
          <div className="text-2xl font-bold text-amber-600">{pendingSignCount}</div>
          <div className="text-xs text-gray-500">รอเซ็นเอกสาร</div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} className="input flex-1">
          <option value="">— ทุกแผนก —</option>
          {departments.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <button onClick={handleRemindMissing} disabled={remindM.isPending} className="btn-cta whitespace-nowrap">
          <Bell className="w-4 h-4" /> ทวงเอกสารที่ขาด
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">กำลังโหลด…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-400 italic">ไม่มีพนักงาน</p>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="border-b border-gray-200 text-gray-500 text-xs">
                <th className="text-left py-2 px-3">พนักงาน</th>
                <th className="text-left py-2 px-2">วันเริ่มงาน</th>
                {requiredTypes.map((t) => (
                  <th key={t} className="text-center py-2 px-2">{EMPLOYEE_DOC_TYPE_LABELS[t]}</th>
                ))}
                <th className="text-left py-2 px-2">รอเซ็น</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((e) => (
                <tr
                  key={e.emp_code}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => setSelectedEmp(e.emp_code)}
                >
                  <td className="py-2 px-3">
                    <div className="font-medium">{e.name}</div>
                    <div className="text-xs text-gray-400">{e.emp_code}{e.department ? ` · ${e.department}` : ''}</div>
                  </td>
                  <td className="py-2 px-2 whitespace-nowrap">
                    {e.start_date ? new Date(e.start_date).toLocaleDateString('th-TH') : '—'}
                  </td>
                  {requiredTypes.map((t) => (
                    <td key={t} className="text-center py-2 px-2">
                      {e.docs[t]?.present ? (
                        <CheckCircle2 className="w-4 h-4 text-green-600 inline" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-400 inline" />
                      )}
                    </td>
                  ))}
                  <td className="py-2 px-2 text-amber-600 font-medium">
                    {e.pending_sign_docs.length > 0
                      ? e.pending_sign_docs.map((d) => `${d.label}${d.version ? ` (ฉบับที่ ${d.version})` : ''}`).join(', ')
                      : <span className="text-gray-300 font-normal">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedEmp && (
        <EmployeeDocumentDetail
          empCode={selectedEmp}
          empName={filtered.find((e) => e.emp_code === selectedEmp)?.name ?? selectedEmp}
          onClose={() => setSelectedEmp(null)}
        />
      )}
    </AdminLayout>
  );
}

function EmployeeDocumentDetail({ empCode, empName, onClose }: { empCode: string; empName: string; onClose: () => void }) {
  const { data: docs, isLoading } = useEmployeeDocuments(empCode);
  const createM = useCreateEmployeeDocument(empCode);
  const genContractM = useGenerateContract();
  const consequence = useConsequence();
  const toast = useToast();

  const [docType, setDocType] = useState<EmployeeDocType>('other');
  const [label, setLabel] = useState('');
  const [uploading, setUploading] = useState(false);

  async function handleFile(file: File | null) {
    if (!file) return;
    setUploading(true);
    try {
      const path = await uploadFile(file, 'identity', file.name);
      await createM.mutateAsync({
        label: label || EMPLOYEE_DOC_TYPE_LABELS[docType],
        r2_path: path,
        mime_type: file.type || undefined,
        size_bytes: file.size,
        doc_type: docType,
      });
      setLabel('');
      toast.success('อัปโหลดเอกสารสำเร็จ');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'อัปโหลดไม่สำเร็จ');
    } finally {
      setUploading(false);
    }
  }

  async function handleGenerateContract() {
    const ok = await consequence({
      title: 'สร้างสัญญาจ้างงาน',
      intro: `ระบบจะสร้างสัญญาจ้าง PDF ให้ ${empName} และแจ้งเตือนให้เข้าไปเซ็นทาง LINE`,
      effects: ['สร้างไฟล์ PDF สัญญาจ้างงาน', 'แจ้งพนักงานทาง LINE ให้เข้าไปเซ็นเอกสาร'],
      confirmText: 'สร้างสัญญา',
    });
    if (!ok) return;
    try {
      const r = await genContractM.mutateAsync({ emp_code: empCode });
      toast.success('สร้างสัญญาจ้างสำเร็จ');
      window.open(r.pdf_url, '_blank');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'สร้างสัญญาไม่สำเร็จ');
    }
  }

  const grouped = useMemo(() => {
    const byType = new Map<string, typeof docs>();
    for (const d of docs ?? []) {
      const key = d.doc_type ?? 'other';
      const list = byType.get(key) ?? [];
      list.push(d);
      byType.set(key, list as NonNullable<typeof docs>);
    }
    return byType;
  }, [docs]);

  return (
    <Modal open onClose={onClose} title={empName} size="max-w-2xl">
      <div className="space-y-4">
        <button onClick={handleGenerateContract} disabled={genContractM.isPending} className="btn-cta w-full">
          <FileSignature className="w-4 h-4" /> {genContractM.isPending ? 'กำลังสร้าง…' : 'สร้างสัญญาจ้างงาน'}
        </button>

        <div className="card p-3 space-y-2">
          <div className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1">
            <Upload className="w-3.5 h-3.5" /> อัปโหลดเอกสารใหม่
          </div>
          <select value={docType} onChange={(e) => setDocType(e.target.value as EmployeeDocType)} className="input w-full">
            {EMPLOYEE_DOC_TYPES.map((t) => (
              <option key={t} value={t}>{EMPLOYEE_DOC_TYPE_LABELS[t]}</option>
            ))}
          </select>
          {docType === 'other' && (
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="ชื่อเอกสาร"
              className="input w-full"
            />
          )}
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            disabled={uploading}
            className="text-sm"
          />
        </div>

        {isLoading ? (
          <p className="text-xs text-gray-400">กำลังโหลด…</p>
        ) : !docs || docs.length === 0 ? (
          <p className="text-xs text-gray-400 italic">ยังไม่มีเอกสาร</p>
        ) : (
          <div className="space-y-3">
            {[...grouped.entries()].map(([type, list]) => (
              <div key={type}>
                <div className="text-xs font-semibold text-gray-500 mb-1">
                  {EMPLOYEE_DOC_TYPE_LABELS[type as EmployeeDocType] ?? type}
                </div>
                <div className="space-y-1">
                  {(list ?? []).map((d) => (
                    <div key={d.id} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="truncate">{d.label} {d.version ? <span className="text-gray-400">v{d.version}</span> : null}</div>
                          <div className="text-[10px] text-gray-400">
                            {new Date(d.uploaded_at).toLocaleDateString('th-TH')}
                            {d.sign_status === 'awaiting' && <span className="text-amber-600 ml-1">รอเซ็น</span>}
                            {d.sign_status === 'signed' && <span className="text-green-600 ml-1">เซ็นแล้ว</span>}
                          </div>
                        </div>
                      </div>
                      <a
                        href={resolveFileUrl(d.url) ?? '#'}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-brand-600 hover:underline flex-shrink-0"
                      >
                        ดู
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
